export const ALL_SPORTS = 'All sports'
export const BASKETBALL = 'basketball'
export const MENS_BASKETBALL = "men's basketball"
export const WOMENS_BASKETBALL = "women's basketball"

const GENDERED_BASKETBALL = [MENS_BASKETBALL, WOMENS_BASKETBALL]

const SPORT_QUERY_ALIASES = {
  mbb: MENS_BASKETBALL,
  wbb: WOMENS_BASKETBALL,
}

export function normalizeFilterValue(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/['’]/g, '')
    .replace(/[_]+/g, ' ')
    .replace(/\s+/g, ' ')
}

export function sportMatchesQuery(label, query) {
  if (!query) return false
  const normalizedLabel = normalizeFilterValue(label)
  const normalizedQuery = normalizeFilterValue(query)
  if (!normalizedLabel || !normalizedQuery) return false
  if (normalizedLabel === normalizedQuery) return true
  // Allow slug-style values like hockey or mens-hockey
  const slugLabel = normalizedLabel.replace(/\s+/g, '-')
  const slugQuery = normalizedQuery.replace(/\s+/g, '-')
  return slugLabel === slugQuery
}

function isBasketballSport(label) {
  return normalizeFilterValue(label) === BASKETBALL
}

// NMSU tags every hoops game as "basketball". Men's and women's show up as
// MBB/WBB, or as mens/womens in the event name. Apostrophes are ignored, so
// "men's" and "women's" count too. "womens" is checked as its own word so it
// is not read as "mens".
function basketballGenderFromText(text, { includeGenderWords = false } = {}) {
  const value = normalizeFilterValue(text)
  if (!value) return null

  const hits = []
  const add = (label, pattern) => {
    const match = pattern.exec(value)
    if (match) hits.push({ label, index: match.index })
  }
  add(WOMENS_BASKETBALL, /\bwbb\b/)
  add(MENS_BASKETBALL, /\bmbb\b/)
  add(WOMENS_BASKETBALL, /\bwomens basketball\b/)
  add(MENS_BASKETBALL, /\bmens basketball\b/)
  if (includeGenderWords) {
    add(WOMENS_BASKETBALL, /\bwomens\b/)
    add(MENS_BASKETBALL, /\bmens\b/)
  }
  if (!hits.length) return null
  hits.sort((a, b) => a.index - b.index)
  return hits[0].label
}

export function eventSport(event) {
  const base =
    event?.sub_categories?.[0]?.name ||
    event?.category?.name ||
    event?.attractions?.[0]?.name ||
    'Events'

  if (!isBasketballSport(base)) {
    return basketballGenderFromText(base) || base
  }

  return (
    basketballGenderFromText(event?.name, { includeGenderWords: true }) ||
    basketballGenderFromText(
      (event?.attractions || [])
        .map((attraction) => attraction?.name)
        .filter(Boolean)
        .join(' ')
    ) ||
    BASKETBALL
  )
}

export function packageSport(pkg) {
  const base = pkg?.category?.name || pkg?.sub_categories?.[0]?.name || null
  if (!base) return null
  return eventSport({
    name: pkg?.name,
    category: { name: base },
    attractions: pkg?.attractions,
  })
}

// Uncategorized items stay visible under every filter rather than vanishing.
// "basketball" includes both men's and women's games.
export function matchesSport(rowSport, selected) {
  if (selected === ALL_SPORTS) return true
  if (!rowSport) return true
  if (rowSport === selected) return true
  return selected === BASKETBALL && GENDERED_BASKETBALL.includes(rowSport)
}

export function orderedSportLabels(rows) {
  const labels = []
  for (const row of rows || []) {
    if (row?.sport && !labels.includes(row.sport)) labels.push(row.sport)
  }

  const hasGender = GENDERED_BASKETBALL.some((label) => labels.includes(label))
  const group = []
  if (hasGender || labels.includes(BASKETBALL)) group.push(BASKETBALL)
  for (const label of GENDERED_BASKETBALL) {
    if (labels.includes(label)) group.push(label)
  }

  const ordered = []
  let placed = false
  for (const label of labels) {
    if (label === BASKETBALL || GENDERED_BASKETBALL.includes(label)) {
      if (!placed) {
        ordered.push(...group)
        placed = true
      }
      continue
    }
    ordered.push(label)
  }
  return ordered
}

export function resolveSportSelection(labels, query) {
  if (!query) return null
  const alias =
    SPORT_QUERY_ALIASES[normalizeFilterValue(query).replace(/\s+/g, '-')]
  if (alias && labels.includes(alias)) return alias
  return labels.find((label) => sportMatchesQuery(label, query)) || null
}
